import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, LineChart, Calendar, IndianRupee, Target, Users, TrendingUp,
  Zap, Link as LinkIcon, User, Building,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const PLATFORM_LABEL = {
  google_ads: 'Google Ads', meta_ads: 'Meta / Facebook Ads', instagram_ads: 'Instagram Ads',
  youtube_ads: 'YouTube Ads', linkedin_ads: 'LinkedIn Ads', justdial: 'JustDial',
  practo: 'Practo', sulekha: 'Sulekha', google_business: 'Google Business',
  email_campaign: 'Email', sms_campaign: 'SMS', whatsapp_campaign: 'WhatsApp',
  influencer: 'Influencer', other_directory: 'Directory', other: 'Other',
};

const STATUS_BADGE = {
  planned: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  ended: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';


export default function DigitalMarketingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();
  const [campaign, setCampaign] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cResp, eResp] = await Promise.all([
          apiFetch(`engage/digital-marketing-campaigns/${id}/`),
          apiFetch('engage/enquiries/'),
        ]);
        if (cancelled) return;
        setCampaign(cResp?.data || cResp?.data?.data || null);
        const all = eResp?.results || eResp?.data?.results || [];
        setEnquiries(all.filter(e => e.source === 'digital_marketing' && String(e.source_details?.campaign_id) === String(id)));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load campaign');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const derived = useMemo(() => {
    if (!campaign) return null;
    const leads = enquiries.length || campaign.linked_enquiries_count || 0;
    const converted = enquiries.filter(e => e.status === 'converted').length || campaign.converted_count || 0;
    const spend = Number(campaign.spend || 0);
    const budget = Number(campaign.budget || 0);
    const cpl = leads > 0 ? spend / leads : 0;
    const cpa = converted > 0 ? spend / converted : 0;
    const convPct = leads ? Math.round(converted / leads * 100) : 0;
    const spendPct = budget > 0 ? Math.round(spend / budget * 100) : 0;
    return { leads, converted, spend, budget, cpl, cpa, convPct, spendPct };
  }, [campaign, enquiries]);

  if (loading && !campaign) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;
  if (!campaign) return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">{error || 'Campaign not found.'}</div>
      <button onClick={() => navigate('/engage/digital-marketing')} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm"><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
    </div>
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/engage/digital-marketing')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronLeft className="w-4 h-4" /> Back to Digital Marketing
        </button>
        <button onClick={() => navigate(`/engage/digital-marketing/${id}/edit`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Edit</button>
      </div>

      <div className="bg-gradient-to-r from-cyan-500/5 to-blue-500/5 dark:from-cyan-500/10 dark:to-blue-500/10 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-sm">
            <LineChart className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{campaign.name}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-sm text-gray-600 dark:text-gray-400">{PLATFORM_LABEL[campaign.platform] || campaign.platform}</span>
              <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[campaign.status] || 'bg-gray-100')}>{campaign.status}</span>
              {campaign.target_department && (
                <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"><Target className="w-3 h-3" /> {campaign.target_department}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {/* Funnel/ROI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Leads</span></div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{derived.leads}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">Converted</span></div><div className="text-xl font-bold mt-1 text-green-600 dark:text-green-400">{derived.converted}<span className="text-xs text-gray-500 ml-1">({derived.convPct}%)</span></div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><Zap className="w-4 h-4 text-purple-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">CPL</span></div><div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">₹ {formatINR(derived.cpl)}</div></div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700"><div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-amber-500" /><span className="text-[10px] uppercase text-gray-500 font-medium">CPA</span></div><div className="text-xl font-bold mt-1 text-amber-600 dark:text-amber-400">₹ {formatINR(derived.cpa)}</div></div>
      </div>

      {/* Spend progress */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Spend vs budget</h3>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            ₹ {formatINR(derived.spend)} of ₹ {formatINR(derived.budget)} · {derived.spendPct}%
          </span>
        </div>
        <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className={cn(
            'h-full transition-all rounded-full',
            derived.spendPct > 90 ? 'bg-red-500' : derived.spendPct > 60 ? 'bg-amber-500' : 'bg-blue-500'
          )} style={{ width: `${Math.min(100, derived.spendPct)}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 space-y-1.5 text-sm">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Campaign details</h3>
          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" />Start: {fmt(campaign.start_date)}</div>
          {campaign.end_date && <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" />End: {fmt(campaign.end_date)}</div>}
          {campaign.owner_name && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-gray-400" />Owner: {campaign.owner_name}</div>}
          {campaign.campaign_url && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <LinkIcon className="w-3.5 h-3.5 text-gray-400" />
              <a href={campaign.campaign_url} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline truncate">{campaign.campaign_url}</a>
            </div>
          )}
          {(campaign.utm_source || campaign.utm_medium || campaign.utm_campaign) && (
            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {campaign.utm_source && <div>utm_source={campaign.utm_source}</div>}
              {campaign.utm_medium && <div>utm_medium={campaign.utm_medium}</div>}
              {campaign.utm_campaign && <div>utm_campaign={campaign.utm_campaign}</div>}
            </div>
          )}
          {campaign.notes && <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700"><p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{campaign.notes}</p></div>}
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="text-sm font-semibold text-gray-900 dark:text-white">Enquiries generated ({enquiries.length})</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Interest</th>
                <th className="px-4 py-2 text-left font-medium">Stage</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {enquiries.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No enquiries linked to this campaign yet.</td></tr>
              ) : enquiries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5"><Link to={`/engage/enquiries/${e.id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{e.name}</Link></td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{e.department_of_interest || '—'}</td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{e.status}</td>
                  <td className="px-4 py-2.5 text-[12px] text-gray-500 dark:text-gray-400">{fmt(e.created_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
